/**
 * Avatar helpers.
 *
 * Backend avatar fields (`LeaderModelView.avatar`, `FacilitatorModelView.avatar`, `Member.avatar`, …)
 * are composed from the Member Management cache and are not guaranteed to arrive as a browser-ready
 * `src`. This normalises the shapes we know about and passes anything else through unchanged, so it
 * can never make a currently-working avatar stop working.
 */

/** base64 magic prefixes → mime type, for payloads stored without a `data:` prefix. */
const BASE64_SIGNATURES: ReadonlyArray<[prefix: string, mime: string]> = [
  ['/9j/', 'image/jpeg'],
  ['iVBORw0KGgo', 'image/png'],
  ['R0lGOD', 'image/gif'],
  ['UklGR', 'image/webp'],
  ['PHN2Zw', 'image/svg+xml'],
];

/**
 * Turn a stored avatar value into something usable as an `<img src>`.
 *
 * Returns `undefined` for empty/blank values so the caller falls back to initials, rather than
 * rendering `<img src="">` (which resolves to the current page and always errors).
 */
export function resolveAvatarSrc(raw?: string | null): string | undefined {
  if (typeof raw !== 'string') return undefined;

  const value = raw.trim();
  if (!value || value === 'null' || value === 'undefined') return undefined;

  // Already a usable URL: absolute, protocol-relative, data: or blob:
  if (/^(https?:)?\/\//i.test(value)) return value;
  if (/^(data|blob):/i.test(value)) return value;

  // Root-relative path — same origin, forwarded by the Vite/Express proxy in dev and nginx in prod
  if (value.startsWith('/')) return value;

  // Bare base64 payload stored without its `data:` prefix
  const signature = BASE64_SIGNATURES.find(([prefix]) => value.startsWith(prefix));
  if (signature) return `data:${signature[1]};base64,${value}`;

  // Unknown shape (e.g. a bare storage key) — pass through untouched.
  return value;
}

const reportedAvatarFailures = new Set<string>();

/**
 * Chain onto a Mantine `<Avatar imageProps={{ onError }}>` so a broken avatar is diagnosable
 * instead of silently degrading to initials. Logs once per URL, dev only.
 *
 * Mantine's own `onError` runs first and flips the component to its placeholder, so this is
 * purely diagnostic — it cannot affect the fallback.
 */
export function reportAvatarLoadError(src?: string | null): void {
  if (!import.meta.env.DEV) return;
  if (!src || reportedAvatarFailures.has(src)) return;
  reportedAvatarFailures.add(src);
  console.warn(
    `[avatar] Image failed to load, falling back to initials:\n  ${src}\n` +
      '  Open that URL in a new tab to see why: 403 = private/expired S3 link, ' +
      '404 = stale record, blocked = mixed content (http image on an https page).',
  );
}

/** Initials for an avatar fallback, e.g. ("Thabo", "Mokoena") → "TM". */
export function getAvatarInitials(firstName?: string | null, lastName?: string | null): string {
  const first = (firstName ?? '').trim().charAt(0);
  const last = (lastName ?? '').trim().charAt(0);
  const initials = `${first}${last}`.toUpperCase();
  return initials || '?';
}

/** Initials from a single display name, e.g. "Thabo Mokoena" → "TM". */
export function getInitialsFromName(fullName?: string | null): string {
  const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return getAvatarInitials(parts[0], parts[parts.length - 1]);
}
